import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaPlus, FaTimes, FaPencilAlt, FaCheck, FaBookOpen, FaFlask,
  FaChevronLeft, FaChevronRight, FaSearch, FaAngleDoubleLeft, FaAngleDoubleRight,
} from 'react-icons/fa';
import {
  listTokens, composeCode, createToken, updateToken, deleteToken
} from '../../../../services/assetCodeTokenService';
import './AssetIDConfiguration.css';

const DEFAULT_TEST_NAME = 'Davit Sockets - Cast-in Socket (Stainless Steel)';
const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];

function AssetIDConfiguration() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testName, setTestName] = useState(DEFAULT_TEST_NAME);
  const [compose, setCompose] = useState({ code: '', matches: [], unmatched: '' });
  const [newRow, setNewRow] = useState({ term: '', code: '', notes: '' });
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({ term: '', code: '', notes: '' });
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sort, setSort] = useState({ key: 'term', dir: 'asc' });
  const composeTimer = useRef();

  const reloadTokens = async () => {
    const res = await listTokens();
    setTokens(res?.tokens || []);
  };

  useEffect(() => {
    (async () => {
      try { await reloadTokens(); } catch (_) { toast.error('Failed to load code tokens'); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (composeTimer.current) clearTimeout(composeTimer.current);
    composeTimer.current = setTimeout(async () => {
      try {
        const r = await composeCode(testName || '');
        setCompose(r || { code: '', matches: [], unmatched: '' });
      } catch (_) {}
    }, 150);
    return () => { if (composeTimer.current) clearTimeout(composeTimer.current); };
  }, [testName, tokens]);

  // Reset to first page whenever filter/page-size changes
  useEffect(() => { setPage(1); }, [filter, pageSize, sort.key, sort.dir]);

  const saveNew = async () => {
    if (!newRow.term.trim() || !newRow.code.trim()) return toast.error('Term and code are both required');
    try {
      await createToken({ term: newRow.term.trim(), code: newRow.code.trim().toUpperCase(), notes: newRow.notes || null });
      setNewRow({ term: '', code: '', notes: '' });
      await reloadTokens();
      toast.success('Token added');
    } catch (e) { toast.error(e.message || 'Failed to add token'); }
  };

  const startEdit = (token) => {
    setEditId(token.id);
    setEditRow({ term: token.term, code: token.code, notes: token.notes || '' });
  };
  const cancelEdit = () => { setEditId(null); setEditRow({ term: '', code: '', notes: '' }); };
  const saveEdit = async () => {
    if (!editRow.term.trim() || !editRow.code.trim()) return toast.error('Term and code are both required');
    try {
      await updateToken(editId, { term: editRow.term.trim(), code: editRow.code.trim().toUpperCase(), notes: editRow.notes || null });
      cancelEdit();
      await reloadTokens();
      toast.success('Token updated');
    } catch (e) { toast.error(e.message || 'Failed to update'); }
  };
  const removeToken = async (id) => {
    if (!window.confirm('Delete this token? Asset names that rely on it will no longer produce its letters.')) return;
    try { await deleteToken(id); await reloadTokens(); toast.success('Token deleted'); }
    catch (e) { toast.error(e.message || 'Failed'); }
  };

  const toggleSort = (key) => {
    setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  const sortedFiltered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const base = q
      ? tokens.filter(t =>
          t.term.toLowerCase().includes(q) ||
          t.code.toLowerCase().includes(q) ||
          (t.notes || '').toLowerCase().includes(q)
        )
      : tokens.slice();
    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    base.sort((a, b) => {
      const av = (a[key] ?? '').toString().toLowerCase();
      const bv = (b[key] ?? '').toString().toLowerCase();
      if (av < bv) return -1 * mul;
      if (av > bv) return  1 * mul;
      return 0;
    });
    return base;
  }, [tokens, filter, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = sortedFiltered.slice(pageStart, pageStart + pageSize);

  // Live tester segment splitter
  const segments = useMemo(() => {
    const src = testName || '';
    if (!compose.matches?.length) return [{ text: src, match: null }];
    const m = [...compose.matches].sort((a, b) => a.index - b.index);
    const out = [];
    let cursor = 0;
    for (const match of m) {
      if (match.index > cursor) out.push({ text: src.slice(cursor, match.index), match: null });
      out.push({ text: src.slice(match.index, match.index + match.term.length), match });
      cursor = match.index + match.term.length;
    }
    if (cursor < src.length) out.push({ text: src.slice(cursor), match: null });
    return out;
  }, [testName, compose]);

  const previewId = compose.code ? `UCL44-${compose.code}-001` : 'UCL44-···-001';
  const sortCaret = (key) => sort.key !== key ? '' : (sort.dir === 'asc' ? ' ↑' : ' ↓');

  return (
    <div className="aic-root">
      <div className="aic-page-header">
        <h1 className="aic-page-title">Asset Code Dictionary</h1>
        <p className="aic-page-subtitle">
          Asset IDs are always <code>PROPERTY — CODE — 001</code>. The middle <strong>code</strong> is built from the asset's sub-type name
          using the token dictionary below. Add or edit tokens to teach the system how to read new phrasings.
        </p>
      </div>

      {/* ——— Live tester ——— */}
      <section className="aic-tester">
        <header className="aic-tester-head">
          <div className="aic-tester-kicker"><FaFlask aria-hidden /> Try the generator</div>
          <div className="aic-tester-help">Paste or type any asset sub-type name — watch the code compose itself.</div>
        </header>

        <input
          className="aic-tester-input"
          value={testName}
          onChange={e => setTestName(e.target.value)}
          placeholder="e.g. Davit Sockets - Cast-in Socket (Stainless Steel)"
          spellCheck="false"
        />

        <div className="aic-tester-split">
          <div className="aic-tester-parse">
            <div className="aic-tester-parse-label">Recognised</div>
            <div className="aic-tester-parse-segments">
              {segments.map((s, i) => (
                <span key={i} className={`aic-parse-seg ${s.match ? 'is-match' : ''}`}>
                  {s.text}
                  {s.match && <sup className="aic-parse-seg-code">{s.match.code}</sup>}
                </span>
              ))}
            </div>
            {compose.matches?.length > 0 && (
              <div className="aic-chip-row">
                {compose.matches.map((m, i) => (
                  <span key={i} className="aic-chip">
                    <span className="aic-chip-term">{m.term}</span>
                    <span className="aic-chip-arrow">→</span>
                    <span className="aic-chip-code">{m.code}</span>
                  </span>
                ))}
              </div>
            )}
            {compose.unmatched && compose.matches?.length > 0 && (
              <div className="aic-tester-warn">
                <strong>Unmatched:</strong> <span>{compose.unmatched}</span> — add a token if this should contribute letters.
              </div>
            )}
          </div>

          <div className="aic-preview-frame">
            <div className="aic-preview-label">Asset ID</div>
            <div className="aic-preview-id">{previewId}</div>
            <div className="aic-preview-hint">
              <span>Property</span> <em>UCL44</em>
              <span>· Code</span> <em>{compose.code || '—'}</em>
              <span>· Sequence</span> <em>001</em>
            </div>
          </div>
        </div>
      </section>

      {/* ——— Token dictionary ——— */}
      <section className="aic-dict">
        <header className="aic-dict-head">
          <div>
            <div className="aic-dict-kicker"><FaBookOpen aria-hidden /> Token dictionary</div>
            <h2 className="aic-dict-title">Terms the system recognises</h2>
            <p className="aic-dict-meta">
              {sortedFiltered.length} of {tokens.length} token{tokens.length !== 1 ? 's' : ''}{filter ? ` matching "${filter}"` : ''}
            </p>
          </div>
          <div className="aic-dict-search">
            <FaSearch className="aic-dict-search-icon" />
            <input
              className="aic-dict-filter"
              placeholder="Filter by term, code, or notes…"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
            {filter && (
              <button className="aic-dict-search-clear" onClick={() => setFilter('')} title="Clear">
                <FaTimes />
              </button>
            )}
          </div>
        </header>

        {/* Inline add-row — stays at the top so it's always reachable */}
        <div className="aic-add-row">
          <div className="aic-add-row-label">Add new</div>
          <input className="aic-input" placeholder="Term, e.g. Stainless Steel"
                 value={newRow.term}
                 onChange={e => setNewRow(r => ({ ...r, term: e.target.value }))}
                 onKeyDown={e => { if (e.key === 'Enter') saveNew(); }} />
          <input className="aic-input aic-input-mono aic-input-code" maxLength={16} placeholder="SS"
                 value={newRow.code}
                 onChange={e => setNewRow(r => ({ ...r, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                 onKeyDown={e => { if (e.key === 'Enter') saveNew(); }} />
          <input className="aic-input" placeholder="Optional notes"
                 value={newRow.notes}
                 onChange={e => setNewRow(r => ({ ...r, notes: e.target.value }))}
                 onKeyDown={e => { if (e.key === 'Enter') saveNew(); }} />
          <button className="aic-btn aic-btn-primary" onClick={saveNew}><FaPlus /> Add</button>
        </div>

        <div className="aic-dict-table">
          <div className="aic-dict-row aic-dict-row-head">
            <button className="aic-sort-btn" onClick={() => toggleSort('term')}>Term{sortCaret('term')}</button>
            <button className="aic-sort-btn" onClick={() => toggleSort('code')}>Code{sortCaret('code')}</button>
            <div>Notes</div>
            <div className="aic-dict-actions-col">Actions</div>
          </div>

          {loading && <div className="aic-dict-empty">Loading…</div>}

          {!loading && pageRows.map(tk => (
            editId === tk.id ? (
              <div key={tk.id} className="aic-dict-row is-editing">
                <input className="aic-input" value={editRow.term}
                       onChange={e => setEditRow(r => ({ ...r, term: e.target.value }))} />
                <input className="aic-input aic-input-mono aic-input-code" maxLength={16}
                       value={editRow.code}
                       onChange={e => setEditRow(r => ({ ...r, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))} />
                <input className="aic-input" value={editRow.notes}
                       onChange={e => setEditRow(r => ({ ...r, notes: e.target.value }))} />
                <div className="aic-dict-actions-col">
                  <button className="aic-btn aic-btn-primary aic-btn-sm" onClick={saveEdit}><FaCheck /> Save</button>
                  <button className="aic-btn aic-btn-ghost aic-btn-sm" onClick={cancelEdit}><FaTimes /></button>
                </div>
              </div>
            ) : (
              <div key={tk.id} className="aic-dict-row">
                <div className="aic-dict-term">{tk.term}</div>
                <div><span className="aic-code-chip">{tk.code}</span></div>
                <div className="aic-dict-notes">{tk.notes || <em className="aic-muted">—</em>}</div>
                <div className="aic-dict-actions-col">
                  <button className="aic-btn aic-btn-ghost aic-btn-sm" onClick={() => startEdit(tk)} title="Edit"><FaPencilAlt /></button>
                  <button className="aic-btn aic-btn-muted aic-btn-sm" onClick={() => removeToken(tk.id)} title="Delete"><FaTimes /></button>
                </div>
              </div>
            )
          ))}

          {!loading && sortedFiltered.length === 0 && (
            <div className="aic-dict-empty">No tokens match "{filter}"</div>
          )}
        </div>

        {/* Pagination */}
        {sortedFiltered.length > 0 && (
          <footer className="aic-pager">
            <div className="aic-pager-info">
              Showing <strong>{pageStart + 1}</strong>–<strong>{Math.min(pageStart + pageSize, sortedFiltered.length)}</strong> of <strong>{sortedFiltered.length}</strong>
            </div>
            <div className="aic-pager-controls">
              <button className="aic-pager-btn" onClick={() => setPage(1)} disabled={currentPage === 1} title="First page"><FaAngleDoubleLeft /></button>
              <button className="aic-pager-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} title="Previous"><FaChevronLeft /></button>
              <span className="aic-pager-page">Page {currentPage} of {totalPages}</span>
              <button className="aic-pager-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} title="Next"><FaChevronRight /></button>
              <button className="aic-pager-btn" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages} title="Last page"><FaAngleDoubleRight /></button>
            </div>
            <div className="aic-pager-size">
              <label>Per page</label>
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </footer>
        )}
      </section>
    </div>
  );
}

export default AssetIDConfiguration;
